import { emptyGeojson } from "../components/riskAssessment/defaultSourceLayer";
import httpClient from "./httpClient";
import { round1 } from "../components/util/tableUtils";
import { apiError } from "./apiError";
import { LngLat } from "react-map-gl";

interface Facility {
  type_id: string;
  address: string;
  location: { type: string; coordinates: [number, number] }; // [lon, lat]
  name: string;
  area_code: string;
  latitude: number;
  longitude: number;
  distance: number; // added by $geoNear
}

interface School extends Facility {
  schoolId: string;
  schoolUnitCode: string;
  grades: string[];
  postalCode: string;
  schoolTypes: string[];
}
interface FireStation extends Facility {}

interface MapboxRoute {
  distance: number; // meters
  duration: number; // seconds
  geometry: { type: string; coordinates: Array<[number, number]> };
}

export interface OneFeature<TFacility> {
  type: string; // "Feature"
  properties: {
    facility: TFacility;
    distance: number;
    duration: number;
    distanceLevel: number;
  };
  geometry: MapboxRoute["geometry"];
}

export const getEnvironmentDistanceSchoolData = async (lngLat: LngLat): Promise<GeoJsonData> => {
  const lastLongitude = lngLat.lng;
  const lastLatitude = lngLat.lat;

  let directionsLoaded;
  try {
    directionsLoaded = await httpClient().get(
      `/directions/walkingFromSchool?lat=${lastLatitude}&lon=${lastLongitude}&maxDistance=50000&maxCount=3`,
    );

    console.log("directionsLoaded", directionsLoaded.data);

    if (!directionsLoaded.data.routes) {
      // 200 OK but no routes
      return emptyGeojson;
    }

    return directionsLoaded.data.routes;
  } catch (error) {
    apiError(error); // 400 or 500 error
    return emptyGeojson;
  }
};

export const getEnvironmentDistanceFireStationData = async (lngLat: LngLat): Promise<GeoJsonData> => {
  const lastLongitude = lngLat.lng;
  const lastLatitude = lngLat.lat;

  let directionsLoaded;
  try {
    directionsLoaded = await httpClient().get(
      `/directions/drivingFromFireStations?lat=${lastLatitude}&lon=${lastLongitude} &maxDistance=50000&maxCount=3`,
    );

    console.log("directionsLoaded", directionsLoaded.data);

    if (!directionsLoaded.data.routes) {
      // 200 OK but no routes
      return emptyGeojson;
    }

    return directionsLoaded.data.routes;
  } catch (error) {
    apiError(error); // 400 or 500 error
    return emptyGeojson;
  }
};

export type EnvironmentDistanceFacilityDataLegendResponse = {
  noData?: string;
  mapReport?: string;
};

export const getEnvironmentDistanceFacilityDataLegend = async (
  lngLat: LngLat,
  directionRoute: string,
): Promise<EnvironmentDistanceFacilityDataLegendResponse> => {
  const lastLongitude = lngLat.lng;
  const lastLatitude = lngLat.lat;

  let directionsLoaded;
  try {
    directionsLoaded = await httpClient().get(
      `/directions/${directionRoute}?lat=${lastLatitude}&lon=${lastLongitude} &maxDistance=50000&maxCount=3`,
    );
  } catch (error) {
    apiError(error); // 400 or 500 error
    return { noData: "" };
  }

  console.log("legend directionsLoaded", directionsLoaded.data);

  if (!directionsLoaded.data.routes) {
    // 200 OK but no routes
    const msg = directionsLoaded.data.message;
    console.error("No routes found:", directionsLoaded.data);
    return { noData: msg };
  }

  const grades = (facility: Facility): string => {
    const school = facility as School;
    if (!school.grades) return "";
    const returnString = `, grades: ${school.grades.join(",")}`;
    console.log("returnString", returnString);
    return returnString;
  };

  const drivingOrWalking = directionRoute.includes("driving") ? "driving" : "walking";

  const routeFeatures: OneFeature<Facility>[] = directionsLoaded.data.routes.features;
  const routeReports: string[] = routeFeatures.map(
    (route) =>
      ` distance ${round1(route.properties.facility.distance / 1000)} [km],` +
      ` ${drivingOrWalking}  ${round1(route.properties.distance / 1000)} [km],` +
      ` ${Math.round(route.properties.duration / 60)} [min]` +
      ` from ${route.properties.facility.name}  ${route.properties.facility.address}${grades(route.properties.facility)}`,
  );
  const mapReport: string = routeReports.join("\n");

  return { mapReport };
};
